import { useEffect, useState } from 'react';
import { BadgeCheck } from 'lucide-react';
import { api, ErrorApi } from '../api';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import { INTERRUPTORES, MODOS_INSTRUMENTO_VENCIDO } from '../ui/calidad';
import { Cargando } from '../ui/EstadoDePantalla';

// AJUSTES DE CALIDAD · un solo interruptor, y por eso una pantalla propia.
//
// VA JUNTO A «Control de movimientos» y no dentro: son dos decisiones
// distintas que toma la misma persona, pero mezclarlas haría que quien
// busca «por qué me pide el lote del proveedor» tuviera que leer una
// pantalla de umbrales de autorización para encontrarlo.
//
// TRES REGLAS DE USUARIO CERO GOBIERNAN ESTO:
//
//   1. **Se dice QUÉ CAMBIA al encenderlo, en una frase y en presente.**
//      Un interruptor que solo dice su nombre obliga a probarlo para
//      saber qué hace, y probarlo aquí significa dejar a un almacén sin
//      poder recibir.
//   2. **Se dice quién lo dejó así y cuándo.** Cuando alguien pregunte
//      «¿desde cuándo me pide esto?», la pantalla contesta sin que nadie
//      tenga que abrir la auditoría.
//   3. **Guardar confirma que ocurrió**, y el aviso dice el efecto, no
//      «Guardado».

// LOS TEXTOS VIENEN POR OMISIÓN, no obligatorios, y es lo que permite que
// la Fase 3 añada dos interruptores más sin tocar ni una llamada anterior
// ni ninguna de las pruebas que ya existían. Es la misma forma que la
// Doctrina 1 exige de un endpoint que gana un campo.
export function TarjetaInterruptor({
  valor, onCambiar, deshabilitado,
  titulo = 'Exigir el lote del proveedor al recibir',
  queCambia = 'Al recibir productos que manejan lotes, el lote del proveedor será obligatorio.',
  siApagado = 'Con esto apagado el campo sigue estando, pero se puede dejar en blanco (es como funcionaba antes).',
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={valor}
        disabled={deshabilitado}
        onChange={(e) => onCambiar(e.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="block text-sm font-semibold text-gray-900">
          {titulo}
        </span>
        <span className="block text-xs text-gray-500">
          {queCambia}
        </span>
        <span className="block text-xs text-gray-500 mt-1">
          {siApagado}
        </span>
      </span>
    </label>
  );
}

export function QuienLoDejoAsi({ por, cuando }) {
  // NUNCA UN HUECO EN BLANCO. «Nadie lo ha cambiado» es una respuesta;
  // un espacio vacío es una pregunta.
  if (!por && !cuando) {
    return (
      <p className="text-xs text-gray-500">
        Nadie lo ha cambiado todavía: está como viene de fábrica.
      </p>
    );
  }
  const fecha = cuando
    ? new Intl.DateTimeFormat('es-MX', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(new Date(cuando))
    : null;
  return (
    <p className="text-xs text-gray-500">
      Lo dejó así <strong>{por || 'alguien que ya no está en el sistema'}</strong>
      {fecha ? <> el {fecha}</> : null}.
    </p>
  );
}


// EL AJUSTE DE LA FASE 5 NO ES UN INTERRUPTOR, y por eso no cabe en
// `TarjetaInterruptor`. «Medir con un instrumento vencido» no se apaga:
// se decide QUÉ HACER, y las dos respuestas son decisiones legítimas —
// una planta de automotriz bloquea, un taller pequeño prefiere que le
// avisen y seguir trabajando. Un sí/no habría obligado a llamar a una de
// las dos «apagado», que no significa nada.
//
// SE PINTAN LAS DOS OPCIONES A LA VEZ, cada una con su consecuencia
// escrita. Una lista desplegable obligaría a abrirla para saber qué hay
// dentro, y a elegir a ciegas entre dos palabras.
export function TarjetaModo({ valor, onCambiar, deshabilitado }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold text-gray-900">
        Si alguien mide con un instrumento cuya calibración se pasó
      </legend>
      {MODOS_INSTRUMENTO_VENCIDO.map(([clave, titulo, queHace]) => (
        <label key={clave} className="flex items-start gap-3">
          <input
            type="radio"
            name="instrumento_vencido"
            value={clave}
            checked={valor === clave}
            disabled={deshabilitado}
            onChange={() => onCambiar(clave)}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900">
              {titulo}
            </span>
            <span className="block text-xs text-gray-500">{queHace}</span>
          </span>
        </label>
      ))}
      <p className="text-xs text-gray-500">
        Esto no cambia nada si tu empresa no tiene instrumentos dados de alta:
        quien inspecciona sigue escribiendo con qué midió, a mano, como
        siempre.
      </p>
    </fieldset>
  );
}


export default function AjustesCalidad() {
  const [cfg, setCfg] = useState(null);
  const [valores, setValores] = useState(null);
  // APARTE DE `valores` a propósito: `valores` son los booleanos de
  // `INTERRUPTORES` y se envían tal cual. Meter aquí una cadena habría
  // hecho que el `!!d[i.campo]` de arriba la convirtiera en `true`.
  const [modo, setModo] = useState('BLOQUEA');
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vigente = true;
    api.get('/calidad/configuracion')
      .then((d) => {
        if (!vigente) return;
        setCfg(d);
        setValores(Object.fromEntries(
          INTERRUPTORES.map((i) => [i.campo, !!d[i.campo]])));
        setModo(d.instrumento_vencido || 'BLOQUEA');
      })
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, []);

  if (error && !cfg) {
    return (
      <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
        {error}
      </div>
    );
  }
  if (!cfg || !valores) return <Cargando filas={4} />;

  const cambiados = INTERRUPTORES.filter((i) => valores[i.campo] !== !!cfg[i.campo]);
  const modoCambiado = modo !== (cfg.instrumento_vencido || 'BLOQUEA');
  const sinCambios = cambiados.length === 0 && !modoCambiado;

  async function guardar(e) {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setGuardando(true);
    try {
      const d = await api.put('/calidad/configuracion',
                              { ...valores, instrumento_vencido: modo });
      setCfg(d);
      setValores(Object.fromEntries(
        INTERRUPTORES.map((i) => [i.campo, !!d[i.campo]])));
      setModo(d.instrumento_vencido || 'BLOQUEA');
      // EL AVISO DICE EL EFECTO, no «Guardado». Quien acaba de encender
      // esto necesita saber qué va a pasar en el andén mañana. Se dice el
      // de cada interruptor que cambió, no uno genérico: cambiar dos y
      // leer una sola frase deja la mitad sin explicar.
      setMsg([
        ...cambiados.map((i) => (d[i.campo] ? i.alEncender : i.alApagar)),
        ...(modoCambiado
          ? [d.instrumento_vencido === 'BLOQUEA'
            ? 'A partir de ahora TEGO no guarda una medida tomada con un instrumento vencido, salvo que alguien con permiso lo autorice explicando por qué.'
            : 'A partir de ahora una medida tomada con un instrumento vencido se guarda, y TEGO escribe en ella que el instrumento estaba vencido.']
          : []),
      ].join(' '));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Ajustes de calidad</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Qué le exige TEGO a quien recibe material y a quien lo embarca.
        </p>
      </div>


      <form onSubmit={guardar} className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <div className="space-y-4">
          {INTERRUPTORES.map((i) => (
            <TarjetaInterruptor
              key={i.campo}
              valor={valores[i.campo]}
              onCambiar={(v) => setValores((a) => ({ ...a, [i.campo]: v }))}
              deshabilitado={guardando}
              titulo={i.titulo}
              queCambia={i.queCambia}
              siApagado={i.siApagado}
            />
          ))}
          <Ayuda id="calidad.trazabilidad" />
        </div>

        {/* LA DE LA FASE 5 VA DEBAJO Y SEPARADA. No es un interruptor
            más: es una decisión de tres líneas sobre otra cosa. */}
        <div className="pt-4 border-t border-gray-100">
          <TarjetaModo
            valor={modo}
            onCambiar={setModo}
            deshabilitado={guardando}
          />
        </div>

        <div className="pt-3 border-t border-gray-100 space-y-2">
          <QuienLoDejoAsi por={cfg.actualizado_por} cuando={cfg.actualizado_en} />

          {error && (
            <p role="alert" className="text-sm text-red-700">{error}</p>
          )}
          {msg && (
            <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-2.5">
              {msg}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={guardando || sinCambios}
              className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4 py-2.5 disabled:opacity-50"
            >
              <BadgeCheck className="h-4 w-4" aria-hidden="true" />
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {/* UN BOTÓN APAGADO DICE POR QUÉ. Sin esto, quien lo ve
                deshabilitado no sabe si es su permiso o su cambio. */}
            {sinCambios && !guardando && (
              <span className="text-xs text-gray-500">
                No has cambiado nada todavía.
              </span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
